import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref as dbRef, get, remove } from 'firebase/database';
import { ref as storageRef, listAll, deleteObject } from 'firebase/storage';
import { database, storage } from '../../Firebase/firebaseconfig';
import { resolveTotal } from './billPDF';
import { ArrowLeft, Eye, Trash2, Receipt } from 'lucide-react';
import './admin-page.css';

export default function ViewBills() {
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const snap = await get(dbRef(database, 'bill'));
      if (snap.exists()) {
        const data = snap.val();
        const parseBillDate = (str) => {
          const parts = String(str || '').split(/[/\-.]/);
          if (parts.length === 3) {
            const [d, m, y] = parts.map(n => parseInt(n, 10));
            if (!isNaN(d) && !isNaN(m) && !isNaN(y)) return new Date(y, m - 1, d).getTime();
          }
          return 0;
        };
        const list = Object.entries(data).map(([invoice, val]) => ({
          invoice,
          ...val,
          resolvedTotal: resolveTotal(val),
        }));
        list.sort((a, b) => {
          const diff = parseBillDate(b.dateOfPurchase) - parseBillDate(a.dateOfPurchase);
          return diff !== 0 ? diff : Number(b.invoice) - Number(a.invoice);
        });
        setBills(list);
      } else {
        setBills([]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (bill) => {
    if (!window.confirm(`Delete bill #${bill.invoice} for ${bill.customerName}? This cannot be undone.`)) return;
    setRemoving(bill.invoice);
    try {
      try {
        const result = await listAll(storageRef(storage, `bills/${bill.invoice}`));
        await Promise.all(result.items.map(item => deleteObject(item)));
      } catch {}
      await remove(dbRef(database, `bill/${bill.invoice}`));
      setBills(prev => prev.filter(b => b.invoice !== bill.invoice));
    } catch (err) {
      setError('Delete failed: ' + err.message);
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="ap-page ap-page-wide">
      <div className="ap-header">
        <button className="ap-back-btn" onClick={() => navigate('/admin/dashboard')}>
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="ap-title">All Bills</h1>
          <p className="ap-subtitle">{bills.length} invoices</p>
        </div>
      </div>

      {error && <div className="ap-error">{error}</div>}

      <div className="ap-search-wrap">
        <input
          className="ap-input"
          placeholder="Search by customer name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 360 }}
        />
      </div>

      <div className="ap-card">
        {loading && <div className="ap-loading">Loading…</div>}
        {!loading && bills.length === 0 && (
          <div className="ap-empty">
            <Receipt size={32} strokeWidth={1.2} style={{ marginBottom: 8, opacity: 0.4 }} />
            <div>No bills found</div>
          </div>
        )}
        {!loading && bills.length > 0 && (() => {
          const filtered = search.trim()
            ? bills.filter(b => (b.customerName || '').toLowerCase().includes(search.toLowerCase()))
            : bills;
          return filtered.length === 0
            ? <div className="ap-empty"><div>No bills match "{search}"</div></div>
            : (
          <div className="ap-list">
            {filtered.map(bill => (
              <div className="ap-list-item" key={bill.invoice} style={{ flexWrap: 'wrap', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="ap-list-item-name">{bill.customerName}</div>
                  <div style={{ fontSize: '0.76rem', color: '#6b7280', marginTop: 2 }}>
                    Invoice #{bill.invoice} &middot; {bill.dateOfPurchase} &middot;{' '}
                    <strong>₹{bill.resolvedTotal.toLocaleString('en-IN')}</strong>
                  </div>
                </div>
                <div className="ap-list-item-actions">
                  <button
                    className="ap-btn ap-btn-ghost"
                    style={{ padding: '6px 12px', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 5 }}
                    onClick={() => navigate(`/admin/dashboard/bill/${bill.invoice}`)}
                  >
                    <Eye size={14} /> View
                  </button>
                  <button
                    className="ap-btn ap-btn-danger"
                    style={{ padding: '6px 12px', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 5 }}
                    disabled={removing === bill.invoice}
                    onClick={() => handleDelete(bill)}
                  >
                    <Trash2 size={14} /> {removing === bill.invoice ? '…' : 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
            );
        })()}
      </div>
    </div>
  );
}
